import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { punishmentsApi } from '../api/punishments';

interface GetPunishmentsParams {
  userId?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export const useMembers = (guildId: string | null, query?: string) => {
  return useQuery({
    queryKey: ['members', guildId, query],
    queryFn: () => punishmentsApi.getMembers(guildId!, query),
    enabled: !!guildId,
  });
};

export const useChannels = (guildId: string | null) => {
  return useQuery({
    queryKey: ['channels', guildId],
    queryFn: () => punishmentsApi.getChannels(guildId!),
    enabled: !!guildId,
  });
};

export const useRoles = (guildId: string | null) => {
  return useQuery({
    queryKey: ['roles', guildId],
    queryFn: () => punishmentsApi.getRoles(guildId!),
    enabled: !!guildId,
  });
};

export const useCreatePunishment = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: string;
      type: 'warn' | 'timeout' | 'kick' | 'ban';
      reason: string;
      duration?: number;
    }) => punishmentsApi.createPunishment(guildId!, data),
    onMutate: async (newPunishment) => {
      await queryClient.cancelQueries({ queryKey: ['punishments', guildId] });

      const previousPunishments = queryClient.getQueryData(['punishments', guildId]);

      // 낙관적 업데이트 - 새 제재 추가
      queryClient.setQueryData(['punishments', guildId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          punishments: [
            {
              _id: 'temp-' + Date.now(),
              guildId: guildId!,
              userId: newPunishment.userId,
              punishmentType: newPunishment.type,
              reason: newPunishment.reason,
              duration: newPunishment.duration,
              timestamp: new Date().toISOString(),
              cancelled: false,
            },
            ...old.punishments,
          ],
          total: old.total + 1,
        };
      });

      return { previousPunishments };
    },
    onError: (_err, _newPunishment, context) => {
      if (context?.previousPunishments) {
        queryClient.setQueryData(['punishments', guildId], context.previousPunishments);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['punishments', guildId] });
    },
  });
};

export const usePunishments = (guildId: string | null, params?: GetPunishmentsParams) => {
  return useQuery({
    queryKey: ['punishments', guildId, params],
    queryFn: () => punishmentsApi.getPunishments(guildId!, params),
    enabled: !!guildId,
  });
};

export const useCancelPunishment = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ punishmentId, reason }: { punishmentId: string; reason: string }) =>
      punishmentsApi.cancelPunishment(guildId!, punishmentId, { reason }),
    onMutate: async ({ punishmentId }) => {
      await queryClient.cancelQueries({ queryKey: ['punishments', guildId] });

      const previousPunishments = queryClient.getQueryData(['punishments', guildId]);

      // 낙관적 업데이트 - 제재 취소 상태로 변경
      queryClient.setQueryData(['punishments', guildId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          punishments: old.punishments.map((p: any) =>
            p._id === punishmentId ? { ...p, cancelled: true } : p
          ),
        };
      });

      return { previousPunishments };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousPunishments) {
        queryClient.setQueryData(['punishments', guildId], context.previousPunishments);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['punishments', guildId] });
    },
  });
};
